import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Alert,
  Button,
  Card,
  Loader,
  Input,
  Title,
  Body,
  Subline,
  Icon,
  Fieldset,
} from '../components/kern';

interface Settings {
  smtp_host: string;
  smtp_port: string;
  smtp_user: string;
  smtp_pass: string;
  smtp_from: string;
  gitlab_url: string;
  gitlab_token: string;
  gitlab_test_project_id: string;
  default_notify_email: string;
}

export function SettingsPage() {
  const { token, user, authMode } = useAuth();
  const [settings, setSettings] = useState<Settings>({
    smtp_host: '',
    smtp_port: '587',
    smtp_user: '',
    smtp_pass: '',
    smtp_from: '',
    gitlab_url: '',
    gitlab_token: '',
    gitlab_test_project_id: '',
    default_notify_email: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [isSavingGitlab, setIsSavingGitlab] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/settings', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Einstellungen');
      }

      const data = await response.json();
      // Map DB key to client state key
      if (data.gitlab_project_id !== undefined) {
        data.gitlab_test_project_id = data.gitlab_project_id;
        delete data.gitlab_project_id;
      }
      setSettings((prev) => ({ ...prev, ...data }));
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten',
      });
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSaveEmailSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingEmail(true);
    setMessage(null);

    try {
      const response = await fetch('/api/v1/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          smtp_host: settings.smtp_host,
          smtp_port: settings.smtp_port,
          smtp_user: settings.smtp_user,
          smtp_pass: settings.smtp_pass,
          smtp_from: settings.smtp_from,
          default_notify_email: settings.default_notify_email,
        }),
      });

      if (!response.ok) {
        throw new Error('Fehler beim Speichern der E-Mail-Einstellungen');
      }

      setMessage({ type: 'success', text: 'E-Mail-Einstellungen erfolgreich gespeichert!' });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten',
      });
    } finally {
      setIsSavingEmail(false);
    }
  };

  const handleSaveGitlabSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingGitlab(true);
    setMessage(null);

    try {
      const response = await fetch('/api/v1/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          gitlab_url: settings.gitlab_url,
          gitlab_token: settings.gitlab_token,
          gitlab_project_id: settings.gitlab_test_project_id,
        }),
      });

      if (!response.ok) {
        throw new Error('Fehler beim Speichern der GitLab-Einstellungen');
      }

      setMessage({ type: 'success', text: 'GitLab-Einstellungen erfolgreich gespeichert!' });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten',
      });
    } finally {
      setIsSavingGitlab(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Die Passwörter stimmen nicht überein.' });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Das Passwort muss mindestens 8 Zeichen lang sein.' });
      return;
    }

    setIsChangingPassword(true);

    try {
      const response = await fetch('/api/v1/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Fehler beim Ändern des Passworts');
      }

      setMessage({ type: 'success', text: 'Passwort erfolgreich geändert!' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten',
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleTestEmail = async () => {
    setMessage(null);

    try {
      const response = await fetch('/api/v1/settings/test-email', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Fehler beim Senden der Test-E-Mail');
      }

      setMessage({ type: 'success', text: 'Test-E-Mail wurde gesendet!' });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten',
      });
    }
  };

  const [isTestingGitLab, setIsTestingGitLab] = useState(false);

  const handleTestGitLab = async () => {
    setMessage(null);

    if (!settings.gitlab_url || !settings.gitlab_token) {
      setMessage({ type: 'error', text: 'Bitte GitLab URL und Token eingeben.' });
      return;
    }

    setIsTestingGitLab(true);

    try {
      const response = await fetch('/api/v1/settings/test-gitlab', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          gitlab_url: settings.gitlab_url,
          gitlab_token: settings.gitlab_token.includes('...') ? undefined : settings.gitlab_token,
          gitlab_project_id: settings.gitlab_test_project_id || undefined,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Fehler bei der GitLab-Verbindung');
      }

      const data = await response.json();
      let text = `GitLab-Verbindung erfolgreich! User: ${data.username}`;
      if (data.testIssue) {
        text += ` — Test-Ticket #${data.testIssue.iid} erstellt und geschlossen: ${data.testIssue.web_url}`;
      }
      setMessage({ type: 'success', text });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten',
      });
    } finally {
      setIsTestingGitLab(false);
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <Loader />
        <Body>Lade Einstellungen...</Body>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <header className="page-header">
        <div>
          <Title>Einstellungen</Title>
          <Subline>Konfiguriere das Feedback-System</Subline>
        </div>
      </header>

      {message && (
        <Alert variant={message.type === 'success' ? 'success' : 'danger'} className="page-alert">
          {message.text}
        </Alert>
      )}

      <div className="kern-container">
        <div className="kern-row">
          {/* User Settings - hidden in OIDC mode */}
          {authMode !== 'oidc' && (
            <div className="kern-col-xl-4 kern-col-md-12">
              <Card className="settings-card">
                <header className="kern-card__header">
                  <Title as="h2" size="small">
                    <Icon name="account" size="small" /> Benutzer
                  </Title>
                </header>
                <section className="kern-card__body">
                  <div className="user-info">
                    <div className="user-avatar">{user?.name?.charAt(0).toUpperCase() || '?'}</div>
                    <div className="user-details">
                      <Body className="user-name">
                        <strong>{user?.name || 'Unbekannt'}</strong>
                      </Body>
                      <Body size="small" className="user-email">
                        {user?.email || '-'}
                      </Body>
                    </div>
                  </div>

                  <form className="password-form" onSubmit={handleChangePassword}>
                    <Fieldset legend="Passwort ändern">
                      <Input
                        id="currentPassword"
                        label="Aktuelles Passwort"
                        type="password"
                        required
                        value={passwordData.currentPassword}
                        onChange={(e) =>
                          setPasswordData({ ...passwordData, currentPassword: e.target.value })
                        }
                      />

                      <Input
                        id="newPassword"
                        label="Neues Passwort"
                        type="password"
                        required
                        minLength={8}
                        value={passwordData.newPassword}
                        onChange={(e) =>
                          setPasswordData({ ...passwordData, newPassword: e.target.value })
                        }
                      />

                      <Input
                        id="confirmPassword"
                        label="Passwort bestätigen"
                        type="password"
                        required
                        value={passwordData.confirmPassword}
                        onChange={(e) =>
                          setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                        }
                      />
                    </Fieldset>

                    <Fieldset legend="">
                      <Button type="submit" variant="primary" disabled={isChangingPassword}>
                        {isChangingPassword ? 'Speichern...' : 'Passwort ändern'}
                      </Button>
                    </Fieldset>
                  </form>
                </section>
              </Card>
            </div>
          )}

          {/* SMTP Settings */}
          <div className="kern-col-xl-4 kern-col-md-12">
            <Card className="settings-card">
              <header className="kern-card__header">
                <Title as="h2" size="small">
                  <Icon name="email" size="small" /> E-Mail (SMTP)
                </Title>
              </header>
              <section className="kern-card__body">
                <form onSubmit={handleSaveEmailSettings}>
                  <div className="form-row">
                    <Input
                      id="smtp_host"
                      label="SMTP Host"
                      value={settings.smtp_host}
                      onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                      placeholder="smtp.example.com"
                    />

                    <Input
                      id="smtp_port"
                      label="Port"
                      value={settings.smtp_port}
                      onChange={(e) => setSettings({ ...settings, smtp_port: e.target.value })}
                      placeholder="587"
                    />
                  </div>

                  <Input
                    id="smtp_user"
                    label="SMTP Benutzer"
                    value={settings.smtp_user}
                    onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })}
                    placeholder="user@example.com"
                  />

                  <Input
                    id="smtp_pass"
                    label="SMTP Passwort"
                    type="password"
                    value={settings.smtp_pass}
                    onChange={(e) => setSettings({ ...settings, smtp_pass: e.target.value })}
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />

                  <Input
                    id="smtp_from"
                    label="Absender-Adresse"
                    type="email"
                    value={settings.smtp_from}
                    onChange={(e) => setSettings({ ...settings, smtp_from: e.target.value })}
                    placeholder="feedback@example.com"
                  />

                  <Input
                    id="default_notify_email"
                    label="Standard-Empfänger"
                    type="email"
                    value={settings.default_notify_email}
                    onChange={(e) =>
                      setSettings({ ...settings, default_notify_email: e.target.value })
                    }
                    placeholder="team@example.com"
                  />

                  <div className="form-actions">
                    <Button type="submit" variant="primary" disabled={isSavingEmail}>
                      {isSavingEmail ? 'Speichern...' : 'Speichern'}
                    </Button>
                    <Button type="button" variant="secondary" onClick={handleTestEmail}>
                      Test-E-Mail senden
                    </Button>
                  </div>
                </form>
              </section>
            </Card>
          </div>

          {/* GitLab Settings */}
          <div className="kern-col-xl-4 kern-col-md-12">
            <Card className="settings-card">
              <header className="kern-card__header">
                <Title as="h2" size="small">
                  <Icon name="source-branch" size="small" /> GitLab
                </Title>
              </header>
              <section className="kern-card__body">
                <form onSubmit={handleSaveGitlabSettings}>
                  <Input
                    id="gitlab_url"
                    label="GitLab URL"
                    type="url"
                    value={settings.gitlab_url}
                    onChange={(e) => setSettings({ ...settings, gitlab_url: e.target.value })}
                    placeholder="https://gitlab.example.com"
                  />

                  <Input
                    id="gitlab_token"
                    label="Access Token"
                    type="password"
                    value={settings.gitlab_token}
                    onChange={(e) => setSettings({ ...settings, gitlab_token: e.target.value })}
                    placeholder="glpat-xxxxxxxxxxxxxxxxxxxx"
                    hint="Benötigt Scopes: api, write_repository"
                  />

                  <Input
                    id="gitlab_test_project_id"
                    label="Projekt-ID (für Ticket-Test)"
                    value={settings.gitlab_test_project_id}
                    onChange={(e) =>
                      setSettings({ ...settings, gitlab_test_project_id: e.target.value })
                    }
                    placeholder="123456"
                    hint="Optional: Erstellt ein Test-Ticket im angegebenen Projekt"
                  />

                  <div className="form-actions">
                    <Button type="submit" variant="primary" disabled={isSavingGitlab}>
                      {isSavingGitlab ? 'Speichern...' : 'Speichern'}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleTestGitLab}
                      disabled={isTestingGitLab}
                    >
                      {isTestingGitLab ? 'Teste...' : 'Verbindung testen'}
                    </Button>
                  </div>
                </form>
              </section>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
